<script setup lang="ts">
import { Eye as EyeIcon, EyeOff as EyeOffIcon, Save as SaveIcon } from '@lucide/vue'
import { storeToRefs } from 'pinia'
import { useField, useForm } from 'vee-validate'
import { ref, onMounted, nextTick } from 'vue'

import BaseSpinner from '@/components/ui/BaseSpinner.vue'
import { showSuccess } from '@/services/toastService'
import { useAuthStore } from '@/stores/authStore'

interface Props {
  token: string
}

interface Emits {
  success: []
  switchToLogin: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Auth store
const authStore = useAuthStore()
const { isLoading, error } = storeToRefs(authStore)

// Password visibility
const showPassword = ref(false)
const showConfirmPassword = ref(false)

// Refs for focus management
const passwordInputRef = ref<HTMLInputElement | null>(null)

// Form setup
const { handleSubmit, resetForm } = useForm()

// Password validation function
const validatePassword = (value: string) => {
  if (!value) {
    return 'Password is required'
  }
  if (value.length < 8) {
    return 'Password must be at least 8 characters long'
  }
  return true
}

const validateConfirmPassword = (value: string) => {
  if (!value) {
    return 'Please confirm your password'
  }
  if (value !== password.value) {
    return 'Passwords do not match'
  }
  return true
}

// Form fields
const {
  value: password,
  errorMessage: passwordError,
  meta: passwordMeta,
} = useField('password', validatePassword)

const {
  value: confirmPassword,
  errorMessage: confirmPasswordError,
  meta: confirmPasswordMeta,
} = useField('confirmPassword', validateConfirmPassword)

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

// Handle form submission
const onSubmit = handleSubmit(async (values) => {
  authStore.clearError()

  const success = await authStore.confirmPasswordReset({
    key: props.token,
    password: values.password,
  })

  if (success) {
    showSuccess('Password reset successfully. You can now log in with your new password.')
    resetForm()
    emit('success')
  }
})

// Switch to login
const handleSwitchToLogin = () => {
  resetForm()
  authStore.clearError()
  emit('switchToLogin')
}

// Focus first input when component mounts
onMounted(() => {
  nextTick(() => {
    if (passwordInputRef.value) {
      passwordInputRef.value.focus()
    }
  })
})
</script>

<template>
  <!-- Add live region for form status announcements -->
  <div id="reset-confirm-status" class="sr-only" aria-live="polite" aria-atomic="true">
    <span v-if="isLoading">Resetting password, please wait...</span>
    <span v-else-if="error">{{ error }}</span>
  </div>

  <form @submit.prevent="onSubmit" class="space-y-4" novalidate>
    <!-- Instructions -->
    <div class="text-center mb-6">
      <p class="text-sm text-gray-600" id="reset-confirm-instructions">
        Enter your new password below. Make sure it's at least 8 characters long.
      </p>
    </div>

    <!-- Password field -->
    <div>
      <label for="reset-confirm-password" class="block text-sm font-medium text-gray-700 mb-2">
        New Password <span class="text-red-500" aria-label="required">*</span>
      </label>
      <div class="relative">
        <input
          id="reset-confirm-password"
          ref="passwordInputRef"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="new-password"
          required
          :class="[
            // Sizing
            'w-full',
            // Spacing
            'px-3',
            'py-2',
            'pr-10',
            // Appearance
            'border',
            'rounded-md',
            // Focus states
            'focus:outline-none',
            'focus:ring-2',
            // Transition
            'transition-colors',
            // Conditional states
            {
              'border-red-300 focus:ring-red-500': passwordError,
              'border-green-300 focus:ring-green-500': passwordMeta.valid && passwordMeta.touched,
              'border-gray-300 focus:ring-primary-500':
                !passwordError && (!passwordMeta.valid || !passwordMeta.touched),
            },
          ]"
          placeholder="Enter your new password"
          :disabled="isLoading"
          :aria-invalid="passwordError ? 'true' : 'false'"
          :aria-describedby="
            passwordError ? 'reset-confirm-password-error' : 'reset-confirm-password-help'
          "
        />
        <button
          type="button"
          @click="togglePasswordVisibility"
          :class="[
            // Position & layout
            'absolute',
            'right-3',
            'top-1/2',
            'transform',
            '-translate-y-1/2',
            'p-1',
            // Colors & interaction
            'text-gray-400',
            'hover:text-gray-600',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            // Shape
            'rounded-md',
          ]"
          :aria-label="showPassword ? 'Hide password' : 'Show password'"
          :aria-pressed="showPassword"
          :disabled="isLoading"
          tabindex="0"
        >
          <EyeOffIcon v-if="showPassword" class="w-5 h-5" />
          <EyeIcon v-else class="w-5 h-5" />
        </button>
      </div>
      <div
        v-if="!passwordError"
        id="reset-confirm-password-help"
        class="mt-1 text-xs text-gray-500"
      >
        Password must be at least 8 characters long
      </div>
      <div
        v-if="passwordError"
        id="reset-confirm-password-error"
        class="mt-1 text-sm text-red-600"
        role="alert"
      >
        {{ passwordError }}
      </div>
    </div>

    <!-- Confirm Password field -->
    <div>
      <label
        for="reset-confirm-password-confirm"
        class="block text-sm font-medium text-gray-700 mb-2"
      >
        Confirm New Password <span class="text-red-500" aria-label="required">*</span>
      </label>
      <div class="relative">
        <input
          id="reset-confirm-password-confirm"
          v-model="confirmPassword"
          :type="showConfirmPassword ? 'text' : 'password'"
          autocomplete="new-password"
          required
          :class="[
            // Sizing
            'w-full',
            // Spacing
            'px-3',
            'py-2',
            'pr-10',
            // Appearance
            'border',
            'rounded-md',
            // Focus states
            'focus:outline-none',
            'focus:ring-2',
            // Transition
            'transition-colors',
            // Conditional states
            {
              'border-red-300 focus:ring-red-500': confirmPasswordError,
              'border-green-300 focus:ring-green-500':
                confirmPasswordMeta.valid && confirmPasswordMeta.touched,
              'border-gray-300 focus:ring-primary-500':
                !confirmPasswordError &&
                (!confirmPasswordMeta.valid || !confirmPasswordMeta.touched),
            },
          ]"
          placeholder="Confirm your new password"
          :disabled="isLoading"
          :aria-invalid="confirmPasswordError ? 'true' : 'false'"
          :aria-describedby="
            confirmPasswordError
              ? 'reset-confirm-password-confirm-error'
              : 'reset-confirm-password-confirm-help'
          "
        />
        <button
          type="button"
          @click="toggleConfirmPasswordVisibility"
          :class="[
            // Position & layout
            'absolute',
            'right-3',
            'top-1/2',
            'transform',
            '-translate-y-1/2',
            'p-1',
            // Colors & interaction
            'text-gray-400',
            'hover:text-gray-600',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            // Shape
            'rounded-md',
          ]"
          :aria-label="showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'"
          :aria-pressed="showConfirmPassword"
          :disabled="isLoading"
          tabindex="0"
        >
          <EyeOffIcon v-if="showConfirmPassword" class="w-5 h-5" />
          <EyeIcon v-else class="w-5 h-5" />
        </button>
      </div>
      <div v-if="!confirmPasswordError" id="reset-confirm-password-confirm-help" class="sr-only">
        Re-enter your new password to confirm
      </div>
      <div
        v-if="confirmPasswordError"
        id="reset-confirm-password-confirm-error"
        class="mt-1 text-sm text-red-600"
        role="alert"
      >
        {{ confirmPasswordError }}
      </div>
    </div>

    <!-- Error message -->
    <div
      v-if="error"
      class="p-3 bg-red-50 border border-red-200 rounded-md"
      role="alert"
      aria-live="polite"
    >
      <p class="text-sm text-red-600">{{ error }}</p>
    </div>

    <!-- Submit button -->
    <button
      type="submit"
      :disabled="isLoading"
      :aria-describedby="isLoading ? 'reset-confirm-loading-status' : undefined"
      :class="[
        // Layout & sizing
        'w-full',
        'flex',
        'items-center',
        'justify-center',
        'px-4',
        'py-2',
        // Colors & states
        'bg-primary-600',
        'hover:bg-primary-700',
        'disabled:bg-primary-400',
        'text-white',
        // Typography & effects
        'font-medium',
        'rounded-md',
        'transition-colors',
        // Focus & interaction
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-primary-500',
        'focus:ring-offset-2',
        'hover:cursor-pointer',
      ]"
    >
      <SaveIcon v-if="!isLoading" class="w-4 h-4 mr-2" aria-hidden="true" />
      <BaseSpinner v-else class="mr-2" aria-hidden="true" />
      {{ isLoading ? 'Resetting Password...' : 'Reset Password' }}
    </button>
    <div v-if="isLoading" id="reset-confirm-loading-status" class="sr-only">
      Please wait while we reset your password
    </div>

    <!-- Footer link -->
    <div class="text-center">
      <div class="flex items-center justify-center space-x-1 text-sm">
        <span class="text-gray-600">Remember your password?</span>
        <button
          type="button"
          @click="handleSwitchToLogin"
          :class="[
            // Colors & interaction
            'text-primary-600',
            'hover:text-primary-800',
            'font-medium',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            // Shape
            'rounded-sm',
          ]"
          :disabled="isLoading"
          aria-describedby="login-help"
        >
          Log in
        </button>
      </div>
      <div id="login-help" class="sr-only">Click to return to the log-in form</div>
    </div>
  </form>
</template>
